import { Component, HostListener, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { UserService } from '@app/core/_services/user.service'
import { firstValueFrom, forkJoin } from 'rxjs'
import { take } from 'rxjs/operators'
import { User } from '@app/core/_models/user'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { BookSessionsService, BookSessionInfo } from '@app/core/_services/book-sessions.service'
import { UtilsService } from '@app/core/_services/utils.service'
import { faRefresh } from '@fortawesome/pro-solid-svg-icons'

import {
    BookSessionPreferences,
    BookSessionsResponse,
    ConfirmedSessionsResponse,
    SESSION_ACTIVITY_TYPE_ANYTHING,
    SESSION_DURATION_25_MINS,
    SESSION_DURATION_50_MINS,
    SESSION_DURATION_75_MINS,
    SavedUserAvailabilityResponse,
    SessionActivityType,
    UserProfileInfo,
} from '@app/core/_models'
import { BookingSource, SessionTileSource } from '@app/core/_services/analytics.service'
import { Title } from '@angular/platform-browser'
import { DialogService } from '@app/core/_services/dialog.service'
import { UserAvailabiltyService } from '@app/core/_services/user-availabilty.service'
import { CancelSessionService, ConfirmedSessionsService } from '@app/core/_services'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import {
    SessionStateService,
    SESSION_STATE_DIRECT_BOOKER,
} from '@app/core/_services/session-state.service'
import {
    DURATION_FILTER_INCLUDE_25,
    DURATION_FILTER_INCLUDE_50,
    DURATION_FILTER_INCLUDE_75,
    DURATION_FILTER_INCLUDE_ALL,
} from '@app/shared-availability-list/components/availability-list-controls/availability-list-controls.component'
import {
    AvailabilityListBookSession,
    AvailabilityListCancelSession,
    AvailabilityListSelectedSession,
    AvailabilitySessionListItem,
} from '@app/shared-availability-list/components/availability-list-item/availability-list-item.component'
import { UserDateFormattingService } from '@app/core/_services/user-date-formatting.service'

const SESSION_DURATION_50_MINS_LABEL: string = '50m'
const SESSION_DURATION_25_MINS_LABEL: string = '25m'
const SESSION_DURATION_75_MINS_LABEL: string = '75m'

interface AvailabilitySessionList {
    dateLabel: string
    sessions: AvailabilitySessionListItem[]
}

type AvailabilityListContentState = 'loading' | 'emptyList' | 'validList'

@Component({
    selector: 'app-availability',
    templateUrl: './availability.component.html',
    styleUrls: ['./availability.component.scss'],
    standalone: false,
})
export class AvailabilityComponent implements OnInit {
    public currentUser: User
    public memberProfile: UserProfileInfo
    public userAvailability: SavedUserAvailabilityResponse
    public invalidUser: boolean = false
    public listContentState: AvailabilityListContentState = 'loading'

    public filteredSessionList: AvailabilitySessionList[] = new Array<AvailabilitySessionList>()
    public completeList: AvailabilitySessionListItem[] = []
    public selectedSession: number = 0
    public disableBookSessions: boolean = false
    public viewingSelf: boolean
    private selectedQuietMode: boolean = false

    public selectedOption = DURATION_FILTER_INCLUDE_ALL
    public faRefresh = faRefresh
    constructor(
        public userService: UserService,
        private userAvailabiltyService: UserAvailabiltyService,
        private route: ActivatedRoute,
        private bookSessionService: BookSessionsService,
        private titleService: Title,
        private dialogService: DialogService,
        private utilsService: UtilsService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private cancelSessionService: CancelSessionService,
        private timeUtils: TimeUtilitiesService,
        private sessionStateService: SessionStateService,
        private dateFormat: UserDateFormattingService,
    ) {}

    @HostListener('click')
    clicked() {
        this.selectedSession = 0
    }

    ngOnInit(): void {
        this.currentUser = this.route.snapshot.data['currentUser']
        this.memberProfile = this.route.snapshot.data['memberProfile']

        if (this.memberProfile.uid === this.currentUser.userId) {
            this.disableBookSessions = true
        }
        this.viewingSelf = this.memberProfile.uid === this.currentUser.userId

        this.queryUserAvailability()

        this.titleService.setTitle(this.memberProfile.firstName + "'s availability - Focusmate")
    }

    reloadSelect() {
        this.queryUserAvailability()
    }

    onQuietModeChanged(e) {
        this.selectedQuietMode = e
        this.populateSessionList()
    }

    private queryUserAvailability() {
        this.listContentState = 'loading'

        this.userAvailabiltyService.getUserSharedAvailability(this.memberProfile.uid).subscribe({
            next: (v) => this.receivedUserAvailability(v),
            error: (e) => this.receivedUserAvailabilityError(e),
        })
    }

    private receivedUserAvailability(userAvailability: SavedUserAvailabilityResponse) {
        this.userAvailability = userAvailability
        this.removeAvailableSessionsFromList()

        for (let meeting of this.userAvailability.availability.meetings) {
            const timeLabel = this.dateFormat.format(
                meeting.sessionTime,
                'h:mma',
                this.currentUser.timeZone,
            )

            this.completeList.push({
                timeLabel,
                externalId: null,
                sessionTime: meeting.sessionTime,
                sessionDuration: meeting.duration,
                sessionDurationLabel: this.getSessionDurationLabel(meeting.duration),
                actionComplete: false,
                confirmed: false,
                cancelConfirm: false,
                sharedAvailability: true,
                saved: true,
                partnerProfileSlug: this.userAvailability.availability.profileUrl,
                partnerDisplayName: this.userAvailability.availability.displayName,
                sessionState: null,
                partnerUserId: this.memberProfile.uid,
                profilePhoto: this.userAvailability.availability.profilePhoto,
                activityType: meeting.activityType,
                quietModeMatchAllowed: this.userAvailability.availability.quietModeMatchAllowed,
                partnerPreferences: meeting.preferences,
            })
        }

        this.queryUserConfirmedSessions()
    }

    private queryUserConfirmedSessions() {
        this.confirmedSessionsService.getConfirmedSessions().subscribe({
            next: (v) => this.receivedConfirmedSessions(v),
            error: (e) => this.receivedConfirmedSessions([]),
        })
    }

    private receivedUserAvailabilityError(e) {
        this.invalidUser = true
    }

    private receivedConfirmedSessions(confirmedSesions: ConfirmedSessionsResponse[]) {
        let confirmedList: ConfirmedSessionsResponse[] = confirmedSesions || []
        this.removeConfirmedSessionsFromList()

        for (let session of confirmedList) {
            if (
                session.partnerId === this.memberProfile.uid &&
                !this.utilsService.hasSessionTimePassed(
                    session.sessionTime,
                    this.utilsService.getCurrentTimestamp(),
                ) &&
                this.sessionStateService.isLockedInState(session.state)
            ) {
                const timeLabel = this.dateFormat.format(
                    session.sessionTime,
                    'h:mma',
                    this.currentUser.timeZone,
                )

                this.completeList.push({
                    timeLabel: timeLabel,
                    externalId: session.externalId,
                    sessionTime: session.sessionTime,
                    sessionDuration: session.duration,
                    sessionDurationLabel: this.getSessionDurationLabel(session.duration),
                    actionComplete: false,
                    confirmed: true,
                    cancelConfirm: false,
                    profilePhoto: session.partnerPhoto,
                    partnerUserId: session.partnerId,
                    partnerDisplayName: session.partnerDisplayName,
                    partnerProfileSlug: session.partnerProfileUrl,
                    saved: session.saved,
                    sharedAvailability: session.sharedAvailability,
                    sessionState: session.state,
                    activityType: session.activityType,
                    quietModeMatchAllowed: true, // We do not receive this info currently from the API, default to true as it won't have any effect here
                    partnerPreferences: session.partnerPreferences,
                })
            }
        }
        this.populateSessionList()
    }

    private removeConfirmedSessionsFromList() {
        this.completeList = this.completeList.filter((session) => session.confirmed !== true)
    }

    private removeAvailableSessionsFromList() {
        this.completeList = this.completeList.filter((session) => session.confirmed !== false)
    }

    private filterSessionBasedOnQuietModeSettings(
        meeting: AvailabilitySessionListItem,
        quietModeSelected: boolean,
    ): boolean {
        /* Do not include sessions in this list if the following conditions are met:
		1. Potential partner has quietModeMatchAllowed setting set to False
		2. Current user has selected quietMode
		3. Potential partner's session is not set to Quiet Mode
		*/
        if (
            !meeting.quietModeMatchAllowed &&
            quietModeSelected &&
            !meeting.partnerPreferences.quietMode.value
        ) {
            return true
        }
        return false
    }

    private populateSessionList() {
        this.filteredSessionList.length = 0
        this.completeList.sort((a, b) => (a.sessionTime < b.sessionTime ? -1 : 1))

        for (let meeting of this.completeList) {
            if (
                !this.filterSessionDuration(meeting.sessionDuration) &&
                !this.filterSessionBasedOnQuietModeSettings(meeting, this.selectedQuietMode)
            ) {
                this.addSessionToList(meeting)
            }
        }

        this.setContentStateEmptyOrValid()
    }

    private filterSessionDuration(duration: number): boolean {
        if (this.selectedOption === DURATION_FILTER_INCLUDE_ALL) {
            return false
        }
        if (
            this.selectedOption === DURATION_FILTER_INCLUDE_50 &&
            duration === SESSION_DURATION_50_MINS
        ) {
            return false
        }
        if (
            this.selectedOption === DURATION_FILTER_INCLUDE_25 &&
            duration === SESSION_DURATION_25_MINS
        ) {
            return false
        }

        if (
            this.selectedOption === DURATION_FILTER_INCLUDE_75 &&
            duration === SESSION_DURATION_75_MINS
        ) {
            return false
        }
        return true
    }

    private addSessionToList(meeting) {
        let currentDate = this.genereateDateLabel(meeting.sessionTime, this.currentUser.timeZone)

        if (
            this.filteredSessionList.length === 0 ||
            this.filteredSessionList[this.filteredSessionList.length - 1].dateLabel !== currentDate
        ) {
            this.filteredSessionList.push({
                dateLabel: currentDate,
                sessions: [meeting],
            })
        } else {
            this.filteredSessionList[this.filteredSessionList.length - 1].sessions.push(meeting)
        }
    }

    durationFilterSelected(value: number): void {
        this.selectedOption = value
        this.populateSessionList()
    }

    private genereateDateLabel(sessionTime: number, timezone: string): string {
        if (this.timeUtils.isToday(sessionTime, timezone)) {
            return moment(sessionTime).tz(timezone).format('[Today], MMMM D')
        } else if (this.timeUtils.isTomorrow(sessionTime, timezone)) {
            return moment(sessionTime).tz(timezone).format('[Tomorrow], MMMM D')
        } else return moment(sessionTime).tz(timezone).format('dddd, MMMM D')
    }

    private setContentStateEmptyOrValid() {
        if (this.filteredSessionList.length === 0) {
            this.listContentState = 'emptyList'
        } else {
            this.listContentState = 'validList'
        }
    }

    private getSessionDurationLabel(sessionDuration: number) {
        if (sessionDuration === SESSION_DURATION_50_MINS) {
            return SESSION_DURATION_50_MINS_LABEL
        } else if (sessionDuration == SESSION_DURATION_75_MINS) {
            return SESSION_DURATION_75_MINS_LABEL
        } else {
            return SESSION_DURATION_25_MINS_LABEL
        }
    }

    public selectSession(selection: AvailabilityListSelectedSession) {
        this.selectedSession = selection.sessionTime
    }

    public bookSession(timeAndDuration: AvailabilityListBookSession) {
        const preferences: BookSessionPreferences = {
            favorites: { value: 'preferred' },
            quietMode: { value: this.selectedQuietMode },
        }

        let sessionTime = timeAndDuration.sessionTime
        let duration = timeAndDuration.duration
        let bookSessionInfo: BookSessionInfo = {
            sessionTime: sessionTime,
            userAvailabilityCode: '',
            duration: duration,
            userId: this.memberProfile.uid,
            title: '',
            preferences,
            activityType: SESSION_ACTIVITY_TYPE_ANYTHING,
        }
        this.disableBookSessions = true

        this.bookSessionService
            .bookSessions(
                [bookSessionInfo],
                this.currentUser.timeZone,
                BookingSource.IndividualAvailabilityList,
            )
            .pipe(take(1))
            .subscribe({
                next: (v) => this.receivedBookSessionResult(timeAndDuration, v),
                error: (e) => this.receivedBookSessionError(sessionTime, e),
                complete: () => this.receivedBookSessionComplete(sessionTime),
            })
    }

    public receivedBookSessionResult(
        timeAndDuration: AvailabilityListBookSession,
        result: BookSessionsResponse[],
    ) {
        let sessionTime = timeAndDuration.sessionTime
        let duration = timeAndDuration.duration
        let parsedRes = this.bookSessionService.parseBookResponse(result)

        if (parsedRes['numSuccess'] !== 0) {
            const newExternalId = result[0].externalId
            this.updateSessionListWithBookedSession(sessionTime, newExternalId)
            this.dialogService.openConfirmTransaction('Session booked!')
        }

        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (parsedRes['sessionConflict'] === true) {
            const conflictTimes = parsedRes['conflicts'].map((conflict) => conflict.sessionTime)
            this.dialogService
                .openSessionConflictWithCancel(conflictTimes, this.currentUser.timeZone)
                .afterClosed()
                .pipe(take(1))
                .subscribe((actionResult) => {
                    if (actionResult.action === 'cancelAndBook') {
                        const preferences: BookSessionPreferences = {
                            favorites: { value: 'preferred' },
                            quietMode: { value: this.selectedQuietMode },
                        }

                        let bookSessionInfo: BookSessionInfo = {
                            sessionTime: sessionTime,
                            userAvailabilityCode: '',
                            duration: duration,
                            userId: this.memberProfile.uid,
                            title: '',
                            preferences,
                            activityType: SESSION_ACTIVITY_TYPE_ANYTHING,
                        }
                        const idsToCancel = parsedRes['conflicts'].map(
                            (conflict) => conflict.externalId,
                        )
                        this.bookAndCancel(bookSessionInfo, idsToCancel)
                    }
                })
        } else if (
            parsedRes['inviterNoLongerAvailable'] === true ||
            parsedRes['alreadyStarted'] === true
        ) {
            this.dialogService.openInviterMeetingNoLongerAvailable()
            this.queryUserAvailability()
        }
    }

    public updateSessionListWithBookedSession(sessionTime: number, externalId: string) {
        /* Booking a session via availabiity list will hardcode activityType to anything */

        this.completeList.forEach((session) => {
            if (session.sessionTime === sessionTime) {
                session.externalId = externalId
                session.confirmed = true
                session.actionComplete = true
                session.sessionState = SESSION_STATE_DIRECT_BOOKER
                session.activityType = SESSION_ACTIVITY_TYPE_ANYTHING
            }
        })

        this.filteredSessionList.forEach((day) => {
            day.sessions.forEach((session) => {
                if (session.sessionTime === sessionTime) {
                    session.externalId = externalId
                    session.confirmed = true
                    session.actionComplete = true
                    session.sessionState = SESSION_STATE_DIRECT_BOOKER
                    session.activityType = SESSION_ACTIVITY_TYPE_ANYTHING
                }
            })
        })
    }

    public receivedBookSessionError(sessionTime: number, e) {
        this.disableBookSessions = false
        this.updateActionComplete(true, sessionTime)
    }

    public receivedBookSessionComplete(sessionTime: number) {
        this.selectedSession = 0
        this.disableBookSessions = false
        this.updateActionComplete(true, sessionTime)
    }

    private bookAndCancel(bookInfo: BookSessionInfo, cancelList: string[]) {
        let cancelRequests = []
        for (let externalId of cancelList) {
            cancelRequests.push(
                this.cancelSessionService.cancelSession(
                    externalId,
                    SessionTileSource.AvailabilityConflictModal,
                ),
            )
        }

        forkJoin(cancelRequests).subscribe({
            next: (v) =>
                this.bookSession({
                    sessionTime: bookInfo.sessionTime,
                    duration: bookInfo.duration,
                    userId: this.memberProfile.uid,
                    activityType: bookInfo.activityType,
                }),
            error: (e) =>
                this.bookSession({
                    sessionTime: bookInfo.sessionTime,
                    duration: bookInfo.duration,
                    userId: this.memberProfile.uid,
                    activityType: bookInfo.activityType,
                }),
        })
    }

    updateActionComplete(complete: boolean, sessionTime: number) {
        for (let day of this.filteredSessionList) {
            for (let session of day.sessions) {
                if (session.sessionTime === sessionTime) {
                    session.actionComplete = complete
                    return
                }
            }
        }
    }

    cancelSession(cancelInfo: AvailabilityListCancelSession) {
        this.cancelSessionService
            .cancelSession(cancelInfo.externalId, SessionTileSource.SeeAvailabilityList)
            .subscribe({
                next: (v) => this.receivedCancelSessionSuccess(cancelInfo.sessionTime),
                error: (e) => this.queryUserAvailability(),
            })
    }

    private receivedCancelSessionSuccess(sessionTime: number) {
        this.updateSessionListWithCanceledSession(sessionTime)

        this.dialogService.openConfirmTransaction('Session canceled')
    }

    private getOriginalActivityTypeFromList(
        availabiityList: SavedUserAvailabilityResponse,
        sessionTime: number,
    ): SessionActivityType | null {
        let session = availabiityList.availability.meetings.find(
            (element) => element.sessionTime == sessionTime,
        )
        return session?.activityType || null
    }

    private async updateSessionListWithCanceledSession(sessionTime: number) {
        /* When canceling a session we must ensure to show the correct
		activity icon for the available session.

		Case 1: When we first loaded the list, this session was not booked and
		we have saved off the original activity type. Update it from our saved list
		Case 2: When we first loaded the list, this session was booked. In this case
		we do not know the activity type the partner had set, so querying it again and
		refresh the list
		*/
        let partnersActivityType = this.getOriginalActivityTypeFromList(
            this.userAvailability,
            sessionTime,
        )

        if (!partnersActivityType) {
            this.userAvailability = await firstValueFrom(
                this.userAvailabiltyService.getUserSharedAvailability(this.memberProfile.uid),
            )
            partnersActivityType = this.getOriginalActivityTypeFromList(
                this.userAvailability,
                sessionTime,
            )
        }

        this.completeList.forEach((session) => {
            if (session.sessionTime === sessionTime) {
                session.externalId = null
                session.confirmed = false
                session.cancelConfirm = false
                session.actionComplete = true
                session.activityType = partnersActivityType
            }
        })

        this.filteredSessionList.forEach((day) => {
            day.sessions.forEach((session) => {
                if (session.sessionTime === sessionTime) {
                    session.externalId = null
                    session.confirmed = false
                    session.cancelConfirm = false
                    session.actionComplete = true
                    session.activityType = partnersActivityType
                }
            })
        })
    }

    dontPropagate(e: Event) {
        e.stopPropagation()
    }

    ngOnDestroy() {
        this.titleService.setTitle('Focusmate - Virtual coworking for getting anything done')
    }
}
